/**
 * App Initialization Composable
 * 
 * Manages the initial app loading state:
 * - Tracks whether app is currently initializing (loading profile)
 * - Provides reactive isInitializing flag for loader display
 * - Only relevant for authenticated routes
 */
import { ref, computed, inject } from 'vue'
import type { Ref } from 'vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { createTokenStorage } from '@space/shared-api-client'
import type { TmaState } from '@/mini-app'

const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
const tokenStorage = createTokenStorage({ storageKey })

// Shared state across all components
const hasInitialized = ref(false)
const isLoadingProfile = ref(false)

export function useAppInitialization() {
    const profileStore = useProfileStore()
    const tmaState = inject<Ref<TmaState>>('tmaState', undefined as any)
    const isInTelegram = inject<boolean>('isInTelegram', false)

    const hasToken = computed(() => !!tokenStorage.getToken())

    const isInitializing = computed(() => {
        // Not initializing if there's no token (public route)
        if (!hasToken.value) {
            return false
        }

        // If already initialized once, don't show loader again
        if (hasInitialized.value) {
            return false
        }

        // Show loader while loading OR if profile not yet loaded
        // This ensures loader shows immediately before fetchProfile starts
        return isLoadingProfile.value || (!profileStore.profile && !hasInitialized.value)
    })

    /**
     * Start initial profile load if needed
     * Call this once from AppLayout on mount
     */
    async function initializeApp() {
        if (!hasToken.value) {
            hasInitialized.value = true
            return
        }

        if (hasInitialized.value) {
            return
        }

        // TMA mode: don't fetch base-info until TMA auth completes.
        // TmaInitPage handles fetchProfile after auth → token → done.
        if (isInTelegram && tmaState?.value && tmaState.value.status !== 'done') {
            hasInitialized.value = true
            return
        }

        // If profile already loaded (e.g., by router guard), skip
        if (profileStore.profile) {
            hasInitialized.value = true
            return
        }

        isLoadingProfile.value = true

        try {
            await profileStore.fetchProfile()
        } catch (error) {
            console.warn('[useAppInitialization] Profile load failed', error)
        } finally {
            isLoadingProfile.value = false
            hasInitialized.value = true
        }
    }

    /**
     * Reset initialization state (e.g., on logout)
     */
    function resetInitialization() {
        hasInitialized.value = false
        isLoadingProfile.value = false
    }

    return {
        isInitializing,
        hasToken,
        initializeApp,
        resetInitialization,
    }
}

